import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const AddUserDialog = ({ isOpen, onClose, onSubmit, calendars = [], initialData = null, isEditing = false, skipEmailCheck = false }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('Super Admin');
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const [canCreateCalendar, setCanCreateCalendar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadedCalendars, setLoadedCalendars] = useState([]);
  const [userId, setUserId] = useState('');
  // Real-time validation states
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  // Email check workflow states
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

    const token = localStorage.getItem('token');
    
  // Check if email exists in backend
  const checkEmailExists = async () => {
    setCheckingEmail(true);
    setError('');
    setEmailChecked(false);
    setEmailExists(false);
    // Clear fields
    setName('');
    setPhone('');
    setRole('Super Admin');
    setSelectedCalendars([]);
    setCanCreateCalendar(false);
    setUserId('');
    try {
      const response = await fetch('/api/ui/appointment/gCal/checkUserByEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'  ,
          'Authorization': `Bearer ${token}`},
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await response.json();
      if (response.ok && data.success && data.user) {
        setName(data.user.name || '');
        setPhone(data.user.phone || '');
        setRole(
          data.user.role === 1 || data.user.role === 'admin' ? 'Admin' : 'Super Admin'
        );
        setUserId(data.user.id || data.user._id || '');
        setEmailExists(true);
      } else if (response.ok && data.success === false && data.message === 'User not found') {
        // No error, just allow fields to be editable
        setEmailExists(false);
      } 
      setEmailChecked(true);
    } catch (err) {
      // Only set error for real network/server errors
      setError('Network error. Please try again.');
    } finally {
      setCheckingEmail(false);
    }
  };
    // Function to fetch calendars
  const fetchCalendars = async () => {    try {
      setIsLoading(true);
      // Get acctNo from localStorage
      const acctNo = localStorage.getItem('acctNo');
      let acctId = '';
      // Get acctId by calling getAccountByAcctNo API
      if (acctNo) {
        try {
          const acctIdResponse = await fetch('/api/ui/appointment/gCal/getAccountByAcctNo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json'  ,
          'Authorization': `Bearer ${token}`},
        body: JSON.stringify({ acctNo })
          });
          if (acctIdResponse.ok) {
            const acctIdData = await acctIdResponse.json();
            if (acctIdData.success && acctIdData.acctId) {
              acctId = acctIdData.acctId;
            }
          }
        } catch (err) {
          console.error('Error fetching acctId from acctNo:', err);
        }
      }
      const userId = localStorage.getItem('userId');
      const response = await fetch('/api/ui/appointment/gCal/getCalendars', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'  ,
          'Authorization': `Bearer ${token}`},
        body: JSON.stringify({ acctId, userId }),
      });
      
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      
     
      // Check for multiple possible response formats
      if (data.success) {
        if (Array.isArray(data.data)) {
          setLoadedCalendars(data.data);
        } else if (Array.isArray(data.calendars)) {
        // Map to proper format expected by the dropdown
          const formattedCalendars = data.calendars.map(cal => ({
            id: cal._id,
            name: cal.calendarName
          }));
          setLoadedCalendars(formattedCalendars);
        } else {
          // If neither format is found, fall back to provided calendars
          setLoadedCalendars(calendars);
        }
      } else {
        // If API response doesn't have data, fall back to provided calendars
        setLoadedCalendars(calendars);
      }
    } catch (error) {
      console.error('Error fetching calendars:', error);
      setError('Failed to load calendars. Please try again.');
      // On error, fall back to provided calendars
      setLoadedCalendars(calendars);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initialize calendars with provided data when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Set initial calendars from props
      setLoadedCalendars(calendars);
      
      // If Admin mode is already selected, fetch fresh calendar data
      if (role === 'Admin') {
        fetchCalendars();
      }
    }
  }, [isOpen, calendars]);
    // Initialize form with user data when editing
  useEffect(() => {
    // If skipEmailCheck is true (editing), show all fields immediately and fill from initialData
    if (isOpen && skipEmailCheck && initialData) {
      setEmailChecked(true);
      setEmailExists(false); // treat as new for editing, allow all fields
      setName(initialData.name || '');
      setEmail(initialData.email || '');
      // Format phone number if it exists
      let formattedPhone = initialData.phone || '';
      if (formattedPhone && !formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }
      // Make sure there's a space after country code if not already formatted
      if (formattedPhone && !formattedPhone.includes(' ') && formattedPhone.length > 3) {
        // Insert a space after the first 1-3 digits (country code)
        const countryCodeLength = Math.min(3, formattedPhone.length - 1);
        formattedPhone = formattedPhone.substring(0, countryCodeLength + 1) + ' ' + 
                        formattedPhone.substring(countryCodeLength + 1);
      }
      setPhone(formattedPhone);
      // Determine admin type from multiple possible sources
      let roleValue = 'Super Admin'; // Default
      if (initialData.role) {
        // Handle both string and numeric role values
        if (initialData.role === 1 || initialData.role === 'admin') {
          roleValue = 'Admin';
        } else if (initialData.role === 0 || initialData.role === 'superadmin' || initialData.role === 'Super Admin') {
          roleValue = 'Super Admin';
        } else {
          // Use directly if it's already in the expected format
          roleValue = initialData.role;
        }
      } else if (initialData.adminType) {
        roleValue = initialData.adminType === 'superadmin' ? 'Super Admin' :
                    initialData.adminType === 'admin' ? 'Admin' :
                    initialData.adminType.charAt(0).toUpperCase() + initialData.adminType.slice(1);
      } else if (initialData.account?.adminType) {
        roleValue = initialData.account.adminType === 'superadmin' ? 'Super Admin' :
                    initialData.account.adminType === 'admin' ? 'Admin' :
                    initialData.account.adminType.charAt(0).toUpperCase() + initialData.account.adminType.slice(1);
      } else if (initialData.roleLabel === 'admin') {
        roleValue = 'Admin';
      } else if (initialData.account?.role === 1) {
        roleValue = 'Admin';
      } else if (initialData.account?.roleLabel === 'admin') {
        roleValue = 'Admin';
      }
      setRole(roleValue);
      // Handle multiple calendars
      if (initialData.calendarIds && Array.isArray(initialData.calendarIds)) {
        setSelectedCalendars(initialData.calendarIds);
      } else if (initialData.calendarId) {
        // Support legacy single calendarId field
        setSelectedCalendars([initialData.calendarId]);
      } else {
        setSelectedCalendars([]);
      }
      setCanCreateCalendar(initialData.canCreateCalendar || false);
      // Make sure we get the user ID from any of the possible fields
      const id = initialData.id || initialData._id || '';
      setUserId(id);
    } else if (isOpen && isEditing && initialData) {
      setName(initialData.name || '');
      setEmail(initialData.email || '');
      // Format phone number if it exists
      let formattedPhone = initialData.phone || '';
      if (formattedPhone && !formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }
      // Make sure there's a space after country code if not already formatted
      if (formattedPhone && !formattedPhone.includes(' ') && formattedPhone.length > 3) {
        // Insert a space after the first 1-3 digits (country code)
        const countryCodeLength = Math.min(3, formattedPhone.length - 1);
        formattedPhone = formattedPhone.substring(0, countryCodeLength + 1) + ' ' + 
                        formattedPhone.substring(countryCodeLength + 1);
      }
      setPhone(formattedPhone);
      // Determine admin type from multiple possible sources
      let roleValue = 'Super Admin'; // Default
      if (initialData.role) {
        // Handle both string and numeric role values
        if (initialData.role === 1 || initialData.role === 'admin') {
          roleValue = 'Admin';
        } else if (initialData.role === 0 || initialData.role === 'superadmin' || initialData.role === 'Super Admin') {
          roleValue = 'Super Admin';
        } else {
          // Use directly if it's already in the expected format
          roleValue = initialData.role;
        }
      } else if (initialData.adminType) {
        roleValue = initialData.adminType === 'superadmin' ? 'Super Admin' :
                    initialData.adminType === 'admin' ? 'Admin' :
                    initialData.adminType.charAt(0).toUpperCase() + initialData.adminType.slice(1);
      } else if (initialData.account?.adminType) {
        roleValue = initialData.account.adminType === 'superadmin' ? 'Super Admin' :
                    initialData.account.adminType === 'admin' ? 'Admin' :
                    initialData.account.adminType.charAt(0).toUpperCase() + initialData.account.adminType.slice(1);
      } else if (initialData.roleLabel === 'admin') {
        roleValue = 'Admin';
      } else if (initialData.account?.role === 1) {
        roleValue = 'Admin';
      } else if (initialData.account?.roleLabel === 'admin') {
        roleValue = 'Admin';
      }
      setRole(roleValue);
      // Handle multiple calendars
      if (initialData.calendarIds && Array.isArray(initialData.calendarIds)) {
        setSelectedCalendars(initialData.calendarIds);
      } else if (initialData.calendarId) {
        // Support legacy single calendarId field
        setSelectedCalendars([initialData.calendarId]);
      } else {
        setSelectedCalendars([]);
      }
      setCanCreateCalendar(initialData.canCreateCalendar || false);
      // Make sure we get the user ID from any of the possible fields
      const id = initialData.id || initialData._id || '';
      setUserId(id);
    } else if (!isEditing) {
      // Reset form when opening in add mode
      setName('');
      setEmail('');
      setPhone('');
      setRole('Super Admin');
      setSelectedCalendars([]);
      setCanCreateCalendar(false);
      setUserId('');
      // Clear validation errors
      setNameError('');
      setEmailError('');
      // setPhoneError removed
    }
  }, [isOpen, isEditing, initialData]);
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setEmail('');
      setPhone('');
      setRole('Super Admin');
      setSelectedCalendars([]);
      setCanCreateCalendar(false);
      setError('');
      setUserId('');
      setNameError('');
      setEmailError('');
      // setPhoneError removed
      setEmailChecked(false);
      setEmailExists(false);
      setCheckingEmail(false);
    }
  }, [isOpen]);
  
  // Fetch calendars when adminType changes to 'Admin'
  useEffect(() => {
    if (isOpen && role === 'Admin') {
      fetchCalendars();
    }
  }, [role, isOpen]);
  
  // Add a new calendar selection
  const addCalendar = () => {
    setSelectedCalendars([...selectedCalendars, '']);
  };

  // Update calendar selection at specific index
  const handleCalendarChange = (index, calendarId) => {
    const newSelectedCalendars = [...selectedCalendars];
    newSelectedCalendars[index] = calendarId;
    setSelectedCalendars(newSelectedCalendars);
  };

  // Remove calendar selection at specific index
  const removeCalendar = (index) => {
    const newSelectedCalendars = [...selectedCalendars];
    newSelectedCalendars.splice(index, 1);
    setSelectedCalendars(newSelectedCalendars);
  };
  
  const validateEmail = (email) => {
    // More comprehensive email validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!email || !email.trim()) {
      return { isValid: false, message: 'Email is required.' };
    }
    if (email.length > 254) {
      return { isValid: false, message: 'Email address is too long.' };
    }
    if (!emailRegex.test(email.trim())) {
      return { isValid: false, message: 'Please enter a valid email address.' };
    }
    return { isValid: true, message: '' };
  };

  // Phone number validation: must be 7-15 digits, numbers only, optional
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) {
      return { isValid: true, message: '' }; // Phone is optional
    }
    const cleanPhone = phone.trim();
    // Only allow numbers (and optional leading +, but we ignore it for validation)
    const digits = cleanPhone.replace(/[^0-9]/g, '');
    if (digits.length < 7 || digits.length > 15) {
      return { isValid: false, message: 'Phone number must be between 7 and 15 digits.' };
    }
    if (!/^\d{7,15}$/.test(digits)) {
      return { isValid: false, message: 'Phone number must contain only digits.' };
    }
    return { isValid: true, message: '' };
  };
  // Real-time validation handlers
  const handleNameChange = (value) => {
    setName(value);
    if (value.trim() === '') {
      setNameError('Name is required.');
    } else if (value.trim().length < 2) {
      setNameError('Name must be at least 2 characters long.');
    } else {
      setNameError('');
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    const validation = validateEmail(value);
    setEmailError(validation.isValid ? '' : validation.message);
    if (!skipEmailCheck) {
      // Hide all fields except email and check button
      setEmailChecked(false);
      setEmailExists(false);
      setName('');
      setPhone('');
      setRole('Super Admin');
      setSelectedCalendars([]);
      setCanCreateCalendar(false);
      setUserId('');
      setNameError('');
      // setPhoneError removed
      setError('');
    }
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    const validation = validatePhoneNumber(value);
    setPhoneError(validation.isValid ? '' : validation.message);
  };

  const validateForm = () => {
    // Reset error
    setError('');

    // Check for any real-time validation errors
    if (nameError || emailError || phoneError) {
      setError('Please fix the validation errors before submitting.');
      return false;
    }

    // Name validation - must not be empty
    if (!name.trim()) {
      setError('Please enter a name.');
      return false;
    }

    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters long.');
      return false;
    }

    // Email validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message);
      return false;
    }

    // Phone validation
    const phoneValidation = validatePhoneNumber(phone);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.message);
      return false;
    }

    // Admin users can have zero or more calendars
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      // Format phone number to remove spaces for storage
      const userData = {
        name: name.trim(),
        email: email.trim(),
        phone: phone,
        role: role === 'Admin' ? 1 : role === 'Super Admin' ? 2 : role,
        roleLabel: role === 'Admin' ? 'admin' : role === 'Super Admin' ? 'superadmin' : role.toLowerCase(),
        acctId: localStorage.getItem('acctId')
      };
      // Add user ID if editing
      if (isEditing && userId) {
  
        userData.id = userId;
      }
      // No password generation - backend will handle user creation without a password from frontend// Add calendar-related fields
      if (role === 'Admin') {
        // Filter out any empty calendar selections for Admin
        const validCalendarIds = selectedCalendars.filter(id => id);
        userData.calendarIds = validCalendarIds;
        // For backward compatibility, also set calendarId to the first selected calendar
        if (validCalendarIds.length > 0) {
          userData.calendarId = validCalendarIds[0];
        }
        userData.canCreateCalendar = canCreateCalendar;
      } else if (role === 'Super Admin') {        // For Super Admin, assign all available calendars
        const allCalendarIds = loadedCalendars.map(cal => cal.id || cal._id);
        userData.calendarIds = allCalendarIds;
        // Set first calendar for backward compatibility if any exist
        if (allCalendarIds.length > 0) {
          userData.calendarId = allCalendarIds[0];
        }
        userData.canCreateCalendar = true; // Super Admin can always create calendars
      }
      

      
      // Call the onSubmit function passed from parent
      await onSubmit(userData);
      
      // Close the dialog on success
      onClose();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'adding'} user:`, error);
      setError(error.message || `Failed to ${isEditing ? 'update' : 'add'} user. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Custom AddUserDialog Workflow ---
  // Only show email, check, cancel, and disabled create user at first
  // After check, show fields as per backend result
  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {}}
        static
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                <div className="relative p-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="border-b border-gray-200 py-4 mb-8">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Edit User' : 'Add New User'}
                  </Dialog.Title>
                </div>
                <form onSubmit={handleSubmit} className="mt-8">
                  {/* Email Field and Check Email Button always editable, check button shown if not checked */}
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <div className="flex flex-row space-x-2">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        className={`block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 sm:text-sm p-2 border ${
                          emailError 
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:border-black focus:ring-black'
                        }`}
                        placeholder="Enter email address"
                        required
                        disabled={skipEmailCheck}
                      />
                      {!skipEmailCheck && !emailChecked && (
                        <button
                          type="button"
                          onClick={checkEmailExists}
                          disabled={checkingEmail || !email.trim() || !!emailError}
                          className={`rounded-md min-w-[120px] ${
                            checkingEmail || !email.trim() || !!emailError
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-black hover:bg-gray-800'
                          } text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                        >
                          {checkingEmail ? 'Checking...' : 'Check Email'}
                        </button>
                      )}
                    </div>
                    {emailError && (
                      <p className="mt-1 text-sm text-red-600">{emailError}</p>
                    )}
                  </div>
                  {/* Show all other fields only after emailChecked or if skipEmailCheck is true */}
                  {(emailChecked || skipEmailCheck) && (
                    <>
                      {/* If email checked and exists, show name/phone (disabled) */}
                      {emailExists ? (
                        <>
                          <div className="mb-4">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name *</label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={name}
                              disabled
                              className="mt-1 block w-full rounded-md shadow-sm p-2 border border-gray-300 bg-gray-100"
                            />
                          </div>
                          <div className="mb-6">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
                            <input
                              type="text"
                              id="phone"
                              name="phone"
                              value={phone}
                              disabled
                              className="mt-1 block w-full rounded-md shadow-sm p-2 border border-gray-300 bg-gray-100"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-4">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name *</label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={name}
                              onChange={(e) => handleNameChange(e.target.value)}
                              className={`mt-1 block w-full rounded-md shadow-sm p-2 border ${nameError ? 'border-red-300' : 'border-gray-300'}`}
                              placeholder="Enter full name"
                              required
                            />
                            {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                          </div>
                          <div className="mb-6">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
                            <div className="relative flex items-center">
                              <span className="absolute left-3 text-gray-500 select-none pointer-events-none">+</span>
                              <input
                                type="text"
                                id="phone"
                                name="phone"
                                value={phone.replace(/^\+/, '')}
                                onChange={e => {
                                  // Only allow up to 15 digits (ignore non-digits for length check)
                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                  if (raw.length <= 15) {
                                    handlePhoneChange('+' + raw);
                                  }
                                }}
                                className={`mt-1 block w-full rounded-md shadow-sm p-2 pl-6 border ${phoneError ? 'border-red-300' : 'border-gray-300'}`}
                                placeholder="Enter phone number (optional)"
                                inputMode="numeric"
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Enter phone number with country code</p>
                            {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
                          </div>
                        </>
                      )}
                      {/* Role Radio Buttons (unchanged) */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                        <div className="flex space-x-6">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="superAdmin"
                              name="role"
                              value="Super Admin"
                              checked={role === 'Super Admin'}
                              onChange={(e) => setRole(e.target.value)}
                              className="focus:ring-black h-4 w-4 text-black border-gray-300"
                            />
                            <label htmlFor="superAdmin" className="ml-2 block text-sm text-gray-900">Super Admin</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="admin"
                              name="role"
                              value="Admin"
                              checked={role === 'Admin'}
                              onChange={(e) => setRole(e.target.value)}
                              className="focus:ring-black h-4 w-4 text-black border-gray-300"
                            />
                            <label htmlFor="admin" className="ml-2 block text-sm text-gray-900">Admin</label>
                          </div>
                        </div>
                      </div>
                      {/* Calendar Selection and Can Create Calendar for Admin */}
                      {role === 'Admin' && (
                        <>
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Assigned Calendars <span className="text-xs font-normal text-gray-500">(You can add multiple calendars)</span>
                            </label>
                            <div className="flex flex-col space-y-2">
                              {selectedCalendars.length === 0 ? (
                                <div className="flex items-center space-x-2">
                                  <div className="flex-1 text-sm text-gray-500 italic rounded-md border-gray-300 border p-2">
                                    No calendars selected. Click the "+" button to add a calendar.
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      type="button"
                                      onClick={addCalendar}
                                      className="rounded-md bg-black text-white p-1 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                      title="Add new calendar"
                                    >
                                      <PlusIcon className="h-4 w-4 stroke-[2]" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {selectedCalendars.map((calendarId, index) => (
                                    <div key={index} className="flex items-center space-x-2">
                                      <select
                                        value={calendarId}
                                        onChange={(e) => handleCalendarChange(index, e.target.value)}
                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-black focus:ring focus:ring-black focus:ring-opacity-50 sm:text-sm p-2 border"
                                      >
                                        <option value="">Select a calendar</option>
                                        {loadedCalendars && loadedCalendars.length > 0 ? (
                                          loadedCalendars
                                            .filter(calendar => {
                                              const calendarIdToCheck = calendar.id || calendar._id;
                                              return calendarId === calendarIdToCheck || !selectedCalendars.some((id, idx) => id === calendarIdToCheck && idx !== index);
                                            })
                                            .map(calendar => (
                                              <option key={calendar.id || calendar._id} value={calendar.id || calendar._id}>
                                                {calendar.name || calendar.calendarName}
                                              </option>
                                            ))
                                        ) : null}
                                      </select>
                                      <div className="flex space-x-1">
                                        <button
                                          type="button"
                                          onClick={() => removeCalendar(index)}
                                          className="rounded-md bg-white text-red-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 p-1"
                                          title="Remove calendar"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                        {index === selectedCalendars.length - 1 && (
                                          <button
                                            type="button"
                                            onClick={addCalendar}
                                            className="rounded-md bg-black text-white p-1 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                            title="Add new calendar"
                                          >
                                            <PlusIcon className="h-4 w-4 stroke-[2]" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}
                              {loadedCalendars.length === 0 && (
                                <div className="mt-1 text-xs text-amber-600">
                                  No calendars found. Please make sure calendars are set up.
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mb-6">
                            <div className="flex items-center">
                              <input
                                id="canCreateCalendar"
                                name="canCreateCalendar"
                                type="checkbox"
                                checked={canCreateCalendar}
                                onChange={(e) => setCanCreateCalendar(e.target.checked)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                              />
                              <label htmlFor="canCreateCalendar" className="ml-2 block text-sm text-gray-900">
                                Can create new calendar
                              </label>
                            </div>
                          </div>
                        </>
                      )}
                      {/* Error Message */}
                      {error && (
                        <div className="mb-4 text-red-500 text-sm">{error}</div>
                      )}
                    </>
                  )}
                  {/* Always show action buttons at the bottom */}
                  <div className="border-t border-gray-200 pt-4 flex justify-between">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isLoading}
                      className="rounded-md bg-gray-200 text-gray-700 py-2 px-4 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        isLoading ||
                        (!emailChecked && !skipEmailCheck) ||
                        ((emailChecked || skipEmailCheck) && !emailExists && (!name.trim() || !role || nameError || emailError || phoneError))
                      }
                      className={`rounded-md ${
                        isLoading ||
                        (!emailChecked && !skipEmailCheck) ||
                        ((emailChecked || skipEmailCheck) && !emailExists && (!name.trim() || !role || nameError || emailError || phoneError))
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-black hover:bg-gray-800'
                      } text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                    >
                      {isLoading ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          {isEditing ? 'Updating...' : 'Adding...'}
                        </div>
                      ) : (
                        isEditing ? 'Update User' : 'Add User'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default AddUserDialog;
